import React, { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { UserData } from "@/entities/users/types";
import { formatMonthYear } from "@/utils/datetime";
import Text from "@/components/ui/text";
import { ChartDataType } from "@/entities/reporting/types";
import { Organization, People, World } from "@dreamhub/designsystem/icons";
import UserAvatar from "@/components/controls/UserAvatar";
import { ChartWidgetType } from "@/components/graphs/WinRate/BarChart";

interface TooltipRowProps {
  icon: ReactNode;
  value: number | string;
  label: string;
  chartWidgetType: ChartWidgetType;
}

export const CustomTooltipArrow = () => {
  return (
    <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[10px] border-l-transparent border-r-[10px] border-r-transparent border-t-[10px] border-t-surface-tooltip-toast"></div>
  );
};
export const TooltipRow: React.FC<TooltipRowProps> = ({
  icon,
  value,
  label,
  chartWidgetType
}) => {
  const { t } = useTranslation();
  const isWinRate = chartWidgetType === "winRate";

  return (
    <div className="grid grid-cols-[24px_auto_1fr] items-center gap-2">
      <div className="flex justify-center items-center text-primary">
        {icon}
      </div>
      <Text>
        {`${value}${isWinRate ? "%" : ""}`}{" "}
        {!isWinRate && t(`leadAnalysis.tooltips.leads`)}
      </Text>
      <Text className="text-tertiary ml-2"> {label}</Text>
    </div>
  );
};

export const LineAndBarChartCustomTooltip: React.FC<{
  data: ChartDataType;
  userData?: UserData;
  showComparison?: boolean;
  chartWidgetType: ChartWidgetType;
  hideIndustry?: boolean;
}> = ({ data, userData, showComparison, chartWidgetType, hideIndustry }) => {
  const { t } = useTranslation();

  const isWinRate = chartWidgetType === "winRate";
  const date = formatMonthYear(data.date);
  const youValue = Math.round(data.youValue);
  const teamValue = Math.round(data.teamValue);
  const organizationValue = Math.round(data.organizationValue);
  const industryValue = !hideIndustry ? Math.round(data.industryValue || 0) : 0;

  return (
    <div>
      <Text
        as="div"
        data-test="lead-analysis-tooltip"
        variant="body"
        className="z-50 max-w-250 overflow-hidden rounded-md border-0 bg-surface-tooltip-toast px-3 py-2 shadow-md "
      >
        <div className={`max-h-${hideIndustry ? 32 : 36} w-full flex flex-col`}>
          <div className="mb-2 text-secondary text-sm">{date}</div>
          {showComparison ? (
            <div className="flex flex-col gap-2">
              <TooltipRow
                icon={
                  <UserAvatar
                    size="M"
                    userData={userData}
                  />
                }
                value={youValue}
                label={t(`chartTooltips.you`)}
                chartWidgetType={chartWidgetType}
              />

              <TooltipRow
                icon={<People />}
                value={teamValue}
                label={t(`chartTooltips.team`)}
                chartWidgetType={chartWidgetType}
              />

              <TooltipRow
                icon={<Organization />}
                value={organizationValue}
                label={t(`chartTooltips.organization`)}
                chartWidgetType={chartWidgetType}
              />

              {!hideIndustry && (
                <TooltipRow
                  icon={<World />}
                  value={industryValue}
                  label={t(`chartTooltips.industry`)}
                  chartWidgetType={chartWidgetType}
                />
              )}
            </div>
          ) : (
            <Text>
              {`${youValue}${isWinRate ? "%" : ""} `}
              {!isWinRate && t(`leadAnalysis.tooltips.leads`)}
            </Text>
          )}
        </div>
      </Text>
      <CustomTooltipArrow />
    </div>
  );
};

export const DirectReportCustomTooltip: React.FC<{
  data: any;
  userMap: Map<string, UserData>;
}> = ({ data, userMap }) => {
  const { month, originalMonth, ...usersTooltipData } = data;

  const date = formatMonthYear(month);
  return (
    <div>
      <Text
        as="div"
        data-test="report-tooltip"
        variant="body"
        className="z-50 max-w-250 overflow-hidden rounded-md border-0 bg-surface-tooltip-toast px-3 py-2 shadow-md "
      >
        <div className="w-full flex flex-col">
          <div className="mb-2 text-secondary text-sm">{date}</div>

          <div className="flex flex-col gap-2">
            {Object.entries(usersTooltipData).map(([userId, value]) => (
              <TooltipRow
                key={userId}
                icon={
                  <UserAvatar
                    size="M"
                    userData={userMap.get(userId)}
                  />
                }
                value={value as number}
                label={userMap.get(userId)?.displayName || userId}
                chartWidgetType="leadAnalysis"
              />
            ))}
          </div>
        </div>
      </Text>
      <CustomTooltipArrow />
    </div>
  );
};
